// Main Lithuanian cities
export const LITHUANIAN_CITIES = [
  'Vilnius',
  'Kaunas',
  'Klaipėda',
  'Šiauliai',
  'Panevėžys',
  'Alytus',
  'Marijampolė',
  'Mažeikiai',
  'Jonava',
  'Utena',
  'Kėdainiai',
  'Telšiai',
  'Tauragė',
  'Ukmergė',
  'Visaginas',
  'Plungė',
  'Kretinga',
  'Palanga',
  'Radviliškis',
  'Druskininkai',
  'Rokiškis',
  'Biržai',
  'Jurbarkas',
  'Garliava',
  'Elektrėnai',
  'Kuršėnai',
  'Gargždai',
  'Skuodas',
  'Varėna',
  'Zarasai',
  'Šilutė',
  'Šilalė',
  'Raseiniai',
  'Prienai',
  'Lazdijai',
  'Kazlų Rūda',
  'Anykščiai',
  'Ignalina',
  'Molėtai',
  'Trakai',
  'Širvintos',
  'Švenčionys',
  'Švenčionėliai',
  'Lentvaris',
  'Nemenčinė',
  'Grigiškės',
  'Kaišiadorys',
  'Šakiai',
  'Vilkaviškis',
];

/** Template vocabulary plus the canonical city list, de-duplicated. */
export function mergeCityOptions(
  templates?: Array<{ id?: number; value: string }> | null,
): Array<{ id: number | string; value: string }> {
  const seen = new Set<string>();
  const out: Array<{ id: number | string; value: string }> = [];
  const add = (value: string, id?: number) => {
    const trimmed = (value || '').trim();
    if (!trimmed || seen.has(trimmed)) return;
    seen.add(trimmed);
    out.push({ id: id ?? trimmed, value: trimmed });
  };
  (templates || []).forEach((city) => add(city.value, city.id));
  LITHUANIAN_CITIES.forEach((name) => add(name));
  return out;
}

// Municipalities grouped by county (apskritis)
export const LITHUANIAN_MUNICIPALITIES: { county: string; municipalities: string[] }[] = [
  {
    county: 'Vilniaus apskritis',
    municipalities: [
      'Vilniaus miesto savivaldybė',
      'Vilniaus rajono savivaldybė',
      'Šalčininkų rajono savivaldybė',
      'Švenčionių rajono savivaldybė',
      'Širvintų rajono savivaldybė',
      'Molėtų rajono savivaldybė',
      'Ukmergės rajono savivaldybė',
      'Elektrėnų savivaldybė',
      'Trakų rajono savivaldybė',
    ],
  },
  {
    county: 'Kauno apskritis',
    municipalities: [
      'Kauno miesto savivaldybė',
      'Kauno rajono savivaldybė',
      'Jonavos rajono savivaldybė',
      'Kėdainių rajono savivaldybė',
      'Prienų rajono savivaldybė',
      'Kaišiadorių rajono savivaldybė',
      'Raseinių rajono savivaldybė',
      'Birštono savivaldybė',
    ],
  },
  {
    county: 'Klaipėdos apskritis',
    municipalities: [
      'Klaipėdos miesto savivaldybė',
      'Klaipėdos rajono savivaldybė',
      'Neringos savivaldybė',
      'Palangos miesto savivaldybė',
      'Skuodo rajono savivaldybė',
      'Kretingos rajono savivaldybė',
      'Šilutės rajono savivaldybė',
    ],
  },
  {
    county: 'Šiaulių apskritis',
    municipalities: [
      'Šiaulių miesto savivaldybė',
      'Šiaulių rajono savivaldybė',
      'Radviliškio rajono savivaldybė',
      'Pakruojo rajono savivaldybė',
      'Joniškio rajono savivaldybė',
      'Kelmės rajono savivaldybė',
      'Akmenės rajono savivaldybė',
    ],
  },
  {
    county: 'Panevėžio apskritis',
    municipalities: [
      'Panevėžio miesto savivaldybė',
      'Panevėžio rajono savivaldybė',
      'Rokiškio rajono savivaldybė',
      'Biržų rajono savivaldybė',
      'Pasvalio rajono savivaldybė',
      'Kupiškio rajono savivaldybė',
    ],
  },
  {
    county: 'Alytaus apskritis',
    municipalities: [
      'Alytaus miesto savivaldybė',
      'Alytaus rajono savivaldybė',
      'Druskininkų savivaldybė',
      'Lazdijų rajono savivaldybė',
      'Varėnos rajono savivaldybė',
    ],
  },
  {
    county: 'Marijampolės apskritis',
    municipalities: [
      'Marijampolės savivaldybė',
      'Kazlų Rūdos savivaldybė',
      'Kalvarijos savivaldybė',
      'Šakių rajono savivaldybė',
      'Vilkaviškio rajono savivaldybė',
    ],
  },
  {
    county: 'Tauragės apskritis',
    municipalities: [
      'Tauragės rajono savivaldybė',
      'Jurbarko rajono savivaldybė',
      'Pagėgių savivaldybė',
      'Šilalės rajono savivaldybė',
    ],
  },
  {
    county: 'Telšių apskritis',
    municipalities: [
      'Telšių rajono savivaldybė',
      'Mažeikių rajono savivaldybė',
      'Plungės rajono savivaldybė',
      'Rietavo savivaldybė',
    ],
  },
  {
    county: 'Utenos apskritis',
    municipalities: [
      'Utenos rajono savivaldybė',
      'Zarasų rajono savivaldybė',
      'Anykščių rajono savivaldybė',
      'Ignalinos rajono savivaldybė',
      'Visagino savivaldybė',
    ],
  },
];

// Flat list of all location options (cities + municipalities) for search/filter use
export const ALL_LITHUANIAN_LOCATIONS = [
  ...LITHUANIAN_CITIES,
  ...LITHUANIAN_MUNICIPALITIES.flatMap(g => g.municipalities),
];

/**
 * The five biggest Lithuanian cities, in canonical display order.
 *
 * Pin these to the top of every city picker — this is what users expect, and
 * relying on backend `display_order` alone is fragile (admin-added rows, seed
 * re-runs, sort tie-breakers, etc. can all silently break the visual order).
 *
 * If you change this list, consider updating
 * `backend/database/migrations/023_seed_cv_templates.sql` as well so the seed
 * stays in sync — but the frontend pinning is what actually guarantees order.
 */
export const MAJOR_CITIES: readonly string[] = [
  'Vilnius',
  'Kaunas',
  'Klaipėda',
  'Šiauliai',
  'Panevėžys',
];

const cityKey = (name: string): string => name.trim().toLocaleLowerCase('lt-LT');

/**
 * Reorder a list of city-like items so that the entries whose `value` matches
 * one of `MAJOR_CITIES` come first, in `MAJOR_CITIES` order. The remaining
 * items keep their original relative order.
 *
 * Generic over the item shape so it works for both
 *  - the DB-loaded `{ id, category, value }` template rows, and
 *  - locally-built fallback rows with the same shape.
 *
 * Pure function — input is not mutated.
 */
export function pinMajorCitiesFirst<T extends { value: string }>(items: T[]): T[] {
  if (!items || items.length === 0) return items;

  const priorityIndex = new Map<string, number>(
    MAJOR_CITIES.map((name, idx) => [cityKey(name), idx]),
  );

  const major: Array<{ item: T; rank: number }> = [];
  const rest: T[] = [];

  for (const item of items) {
    const rank = priorityIndex.get(cityKey(item.value));
    if (rank !== undefined) {
      major.push({ item, rank });
    } else {
      rest.push(item);
    }
  }

  major.sort((a, b) => a.rank - b.rank);
  return [...major.map(m => m.item), ...rest];
}

/**
 * Split a list of city-like items into a `major` group (matching `MAJOR_CITIES`,
 * in `MAJOR_CITIES` order) and a `rest` group (everything else, original order).
 *
 * Use this when the UI needs to render the two groups separately — e.g. an
 * `<optgroup>` in a `<select>`, or a checkbox list with a visual divider
 * between the pinned top cities and the rest.
 *
 * Pure function — input is not mutated.
 */
export function splitMajorCities<T extends { value: string }>(
  items: T[],
): { major: T[]; rest: T[] } {
  if (!items || items.length === 0) return { major: [], rest: [] };

  const priorityIndex = new Map<string, number>(
    MAJOR_CITIES.map((name, idx) => [cityKey(name), idx]),
  );

  const ranked: Array<{ item: T; rank: number }> = [];
  const rest: T[] = [];

  for (const item of items) {
    const rank = priorityIndex.get(cityKey(item.value));
    if (rank !== undefined) {
      ranked.push({ item, rank });
    } else {
      rest.push(item);
    }
  }

  ranked.sort((a, b) => a.rank - b.rank);
  return { major: ranked.map(r => r.item), rest };
}

/**
 * String-flavoured sibling of `pinMajorCitiesFirst` for plain `string[]`
 * inputs (e.g. the `<optgroup>` in `EmployeeProfile`).
 */
export function pinMajorCityNamesFirst(names: readonly string[]): string[] {
  if (!names || names.length === 0) return [];
  const priorityIndex = new Map<string, number>(
    MAJOR_CITIES.map((name, idx) => [cityKey(name), idx]),
  );

  const major: Array<{ name: string; rank: number }> = [];
  const rest: string[] = [];

  for (const name of names) {
    const rank = priorityIndex.get(cityKey(name));
    if (rank !== undefined) {
      major.push({ name, rank });
    } else {
      rest.push(name);
    }
  }

  major.sort((a, b) => a.rank - b.rank);
  return [...major.map(m => m.name), ...rest];
}
